/**
 * This is the date picker widget
 *
 * optional @prop onSelectDate {Function} process a selected date
 * optional @prop widgetId {String} widget ID
 * optional @prop selectedDate {Date} selected date value
 * optional @prop minDate {Date} The minimum date value
 * optional @prop maxDate {Date} The maximum date value
 * optional @prop locale {String} locale string
 * optional @prop headerDateFormat {String} header momentJS format
 *
 * return date: {Date} selected date JS Date format
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { omit } from 'lodash';
import moment from 'moment';
import displayName from 'component-lib/displayName';

import KillEvent from '../../lib/KillEvent';

import Divider from './Divider';
import DatepickerHeader from './Datepicker/Header';
import DatepickerWeekdays from './Datepicker/Weekdays';
import DatepickerBody from './Datepicker/Body';

import styles from './Datepicker.scss';

export default class Datepicker extends Component {
    static displayName = displayName('Widgets/Datepicker');

    static propTypes = {
        onSelectDate: PropTypes.func,
        selectedDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
        minDate: PropTypes.instanceOf(Date),
        maxDate: PropTypes.instanceOf(Date),
        locale: PropTypes.string,
        headerDateFormat: PropTypes.string,
        selectedDateFormat: PropTypes.string,
        widgetId: PropTypes.string,
    };

    static defaultProps = {
        locale: 'en-us',
        headerDateFormat: 'MMM YYYY',
        selectedDate: null,
        minDate: null,
        maxDate: null,
        onSelectDate: () => {},
        widgetId: '',
        selectedDateFormat: '',
    };

    constructor(props) {
        super(props);

        const currentDate = moment(props.selectedDate).isValid()
            ? moment(props.selectedDate)
            : this.validateDate(moment());

        this.state = {
            currentDate: currentDate.startOf('month'),
        };

        this.onPreviousMonthClick = this.onPreviousMonthClick.bind(this);
        this.onNextMonthClick = this.onNextMonthClick.bind(this);
        this.onPreviousYearClick = this.onPreviousYearClick.bind(this);
        this.onNextYearClick = this.onNextYearClick.bind(this);

        moment.locale(this.props.locale);
    }

  @KillEvent
    onPreviousMonthClick() {
        this.setState({
            currentDate: moment(this.state.currentDate).subtract(1, 'months'),
        });
    }

  @KillEvent
  onNextMonthClick() {
      this.setState({
          currentDate: moment(this.state.currentDate).add(1, 'months'),
      });
  }

  @KillEvent
  onPreviousYearClick() {
      const newDate = this.validateDate(moment(this.state.currentDate).subtract(1, 'years'));

      this.setState({
          currentDate: newDate.startOf('month'),
      });
  }

  @KillEvent
  onNextYearClick() {
      const newDate = this.validateDate(moment(this.state.currentDate).add(1, 'years'));

      this.setState({
          currentDate: newDate.startOf('month'),
      });
  }

  validateDate(date) {
      let newDate = moment(date);

      if (this.props.minDate) {
          const minDate = moment(this.props.minDate).startOf('month');

          newDate = date.endOf('month').isBefore(minDate) ? minDate : newDate;
      }

      if (this.props.maxDate) {
          const maxDate = moment(this.props.maxDate).endOf('month');

          newDate = date.startOf('month').isAfter(maxDate) ? maxDate : newDate;
      }

      return newDate.startOf('month');
  }

  checkArrowsVisibility() {
      const currentDate = moment(this.state.currentDate);
      const isPrevArrowsVisible = this.props.minDate
          ? moment(this.props.minDate) < currentDate.startOf('month')
          : true;
      const isNextArrowsVisible = this.props.maxDate
          ? moment(this.props.maxDate) > currentDate.endOf('month')
          : true;

      return { isPrevArrowsVisible, isNextArrowsVisible };
  }

  render() {
      const arrowsVisibility = this.checkArrowsVisibility();
      const currentDate = moment(this.state.currentDate);
      const dateRange = {
          min: this.props.minDate,
          max: this.props.maxDate,
      };
      const widgetProps = omit(this.props, [
          'onSelectDate',
          'selectedDate',
          'minDate',
          'maxDate',
          'styleName',
          'styles',
          'headerDateFormat',
          'widgetId',
          'selectedDateFormat',
      ]);

      return (
          <div {...widgetProps} data-widget={`datepicker-${this.props.widgetId}`}>
              <div className={styles['blocks-wrapper']}>
                  <DatepickerHeader
                      currentDate={currentDate}
                      onPreviousMonth={this.onPreviousMonthClick}
                      onNextMonth={this.onNextMonthClick}
                      onPreviousYear={this.onPreviousYearClick}
                      onNextYear={this.onNextYearClick}
                      arrowsVisibility={arrowsVisibility}
                      dateFormat={this.props.headerDateFormat}
                  />

                  <Divider />

                  <DatepickerWeekdays />

                  <Divider className={styles['second-divider']} />

                  <DatepickerBody
                      currentDate={currentDate}
                      dateRange={dateRange}
                      selectedDate={moment(this.props.selectedDate)}
                      selectedDateFormat={this.props.selectedDateFormat}
                      onSelectDate={this.props.onSelectDate}
                  />
              </div>
          </div>
      );
  }
}
